import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Phone, ShieldCheck, Loader2, KeyRound, Lock, AlertTriangle,
  CheckCircle2, Clock,
} from "lucide-react";
import { API } from "../App";

const COOLDOWN_DAYS = 30;

const formatPhoneInput = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : "";
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const cooldownRemaining = (lastIso) => {
  if (!lastIso) return null;
  const last = new Date(lastIso);
  if (Number.isNaN(last.getTime())) return null;
  const elapsed = Math.floor((Date.now() - last.getTime()) / 86400000);
  const left = COOLDOWN_DAYS - elapsed;
  return left > 0 ? left : null;
};

/**
 * Self-service phone change modal — 2-step flow (form → OTP).
 * Server enforces 30-day cooldown + CPF re-confirmation; UI mirrors
 * the same rules for instant feedback.
 */
export default function PhoneChangeDialog({
  open, onOpenChange, token, profile, onChanged,
}) {
  const [step, setStep] = useState("form"); // form | otp | done
  const [cpfConfirm, setCpfConfirm] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [phoneE164, setPhoneE164] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("form");
    setCpfConfirm("");
    setNewPhone("");
    setCode("");
    setPhoneE164("");
    setBusy(false);
  }, [open]);

  const cooldown = cooldownRemaining(profile?.telefone_changed_at);
  const isLocked = cooldown !== null;

  const requestChange = async () => {
    const cpfDigits = cpfConfirm.replace(/\D/g, "");
    const phoneDigits = newPhone.replace(/\D/g, "");
    if (cpfDigits.length !== 11) {
      toast.error("Senha inválida — informe os 11 dígitos do CPF.");
      return;
    }
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      toast.error("Telefone inválido. Use DDD + número.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/employee/profile/phone-change/request`,
        { token, cpf_confirm: cpfDigits, new_phone: phoneDigits },
        { timeout: 30000 },
      );
      setPhoneE164(res.data?.new_phone_e164 || "");
      setStep("otp");
      toast.success("SMS enviado para o novo número.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar troca.");
    } finally {
      setBusy(false);
    }
  };

  const confirmChange = async () => {
    if (code.trim().length < 4) {
      toast.error("Digite o código recebido por SMS.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/employee/profile/phone-change/confirm`,
        { token, code: code.trim() },
        { timeout: 30000 },
      );
      setStep("done");
      toast.success("Telefone atualizado com sucesso.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={busy ? () => {} : onOpenChange}>
      <DialogContent className="max-w-md" data-testid="phone-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Phone className="w-5 h-5 text-sky-600" />
            Trocar Telefone
          </DialogTitle>
          <DialogDescription>
            Por segurança, você precisa confirmar seu CPF e validar o
            novo número por SMS antes que a alteração seja aplicada.
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {isLocked && step === "form" ? (
            <motion.div
              key="locked"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-amber-50 border border-amber-200 rounded-md p-3"
            >
              <div className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">Troca em cooldown</p>
                  <p>
                    Você só poderá trocar seu telefone novamente em{" "}
                    <strong>{cooldown} dia(s)</strong>. Para urgências,
                    entre em contato com o administrador.
                  </p>
                </div>
              </div>
            </motion.div>
          ) : null}

          {!isLocked && step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-3"
            >
              <div className="bg-sky-50 border border-sky-200 rounded p-2 text-xs text-sky-900">
                <strong>Importante:</strong> após a troca, você só
                poderá alterar o número novamente daqui a{" "}
                <strong>{COOLDOWN_DAYS} dias</strong>.
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cpf-confirm">Insira sua senha</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="cpf-confirm"
                    type="password"
                    inputMode="numeric"
                    autoComplete="current-password"
                    maxLength={11}
                    placeholder="••••••••"
                    className="pl-9 font-mono tracking-widest"
                    value={cpfConfirm.replace(/\D/g, "")}
                    onChange={(e) =>
                      setCpfConfirm(e.target.value.replace(/\D/g, ""))
                    }
                    data-testid="phone-change-cpf-input"
                  />
                </div>
                <p className="text-[11px] text-slate-500">
                  Sua senha são os 11 dígitos do seu CPF.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-phone">Novo telefone (com DDD)</Label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="new-phone"
                    inputMode="tel"
                    placeholder="(71) 99999-9999"
                    className="pl-9 font-mono"
                    value={formatPhoneInput(newPhone)}
                    onChange={(e) => setNewPhone(e.target.value)}
                    data-testid="phone-change-new-phone-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Enviaremos um código de 5 dígitos por SMS para
                  validar este número antes de salvar.
                </p>
              </div>
            </motion.div>
          )}

          {step === "otp" && (
            <motion.div
              key="otp"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-3"
            >
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-900">
                <ShieldCheck className="w-4 h-4 inline mr-1" />
                SMS enviado para{" "}
                <span className="font-mono font-semibold">{phoneE164}</span>.
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="otp-code">Código recebido</Label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="otp-code"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="00000"
                    className="pl-9 tracking-widest font-mono text-lg"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="phone-change-otp-input"
                    autoFocus
                  />
                </div>
              </div>
            </motion.div>
          )}

          {step === "done" && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-3"
            >
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              <p className="font-semibold text-slate-900">Telefone alterado!</p>
              <p className="text-sm text-slate-500 mt-1">
                Seu RH foi notificado da alteração. Próxima troca
                permitida em {COOLDOWN_DAYS} dias.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <DialogFooter className="gap-2">
          {step === "form" && !isLocked && (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancelar
              </Button>
              <Button
                onClick={requestChange}
                disabled={busy}
                data-testid="phone-change-request-btn"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-1" />
                    Enviar SMS
                  </>
                )}
              </Button>
            </>
          )}
          {step === "form" && isLocked && (
            <Button onClick={() => onOpenChange(false)}>Entendi</Button>
          )}
          {step === "otp" && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep("form")}
                disabled={busy}
              >
                Voltar
              </Button>
              <Button
                onClick={confirmChange}
                disabled={busy}
                data-testid="phone-change-confirm-btn"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Confirmando…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-1" />
                    Confirmar troca
                  </>
                )}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button onClick={() => onOpenChange(false)}>
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
